import { Sparkles } from "lucide-react";

type Variant = "pre_call" | "loading_no_payload" | "in_call_empty";

type Props = {
    variant: Variant;
};

/**
 * États "rien à afficher" :
 *
 * - `pre_call` : tab ouverte alors qu'aucun appel n'est en cours et
 *   qu'aucun catalogue n'a encore été poussé. Message d'attente
 *   orienté "ça arrive au début de l'appel".
 * - `loading_no_payload` : `isSmartCatalogPending && smartCatalog ===
 *   null` — premier batch en vol au démarrage de l'appel. On affiche
 *   un skeleton léger (5 × 2 cartes) plutôt qu'un loader pleine page
 *   pour que l'agent voie que ça arrive sans flash agressif.
 * - `in_call_empty` : appel en cours, aucun catalogue chargé et rien
 *   en vol (typiquement après l'échec d'un refresh/override). On invite
 *   à relancer la génération via le bouton de l'en-tête.
 */
export function SmartCatalogEmptyState({ variant }: Props) {
    if (variant === "loading_no_payload") {
        return (
            <div className="flex flex-col gap-4" aria-busy="true" aria-live="polite">
                {[0, 1].map(col => (
                    <div key={col} className="flex flex-col gap-2">
                        <div className="h-4 w-32 animate-pulse rounded bg-gray-100" />
                        <div className="flex flex-col gap-2">
                            {[0, 1, 2, 3, 4].map(row => (
                                <div key={row} className="flex animate-pulse flex-col gap-2 rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
                                    <div className="h-3 w-2/3 rounded bg-gray-100" />
                                    <div className="h-2.5 w-1/2 rounded bg-gray-100" />
                                    <div className="h-10 w-full rounded bg-gray-50" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                <p className="text-sm text-gray-500">Chargement en cours…</p>
            </div>
        );
    }

    if (variant === "in_call_empty") {
        return (
            <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
                <Sparkles className="mb-2 size-5 text-gray-400" aria-hidden />
                <p className="text-sm text-gray-600">Aucun catalogue disponible pour le moment.</p>
                <p className="mt-1 text-sm text-gray-500">Utilisez « Mettre à jour le catalogue » pour relancer la génération.</p>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
            <p className="text-sm text-gray-600">Les éléments apparaîtront au fil de l&apos;appel.</p>
        </div>
    );
}
